import { Link } from 'react-router-dom';

import { brand } from '../../config/brand';
import { legal } from '../../config/legal';
import { Clause, LegalPage, Points } from './LegalPage';
import { useDocumentTitle } from '../../lib/title';

/**
 * The terms of service.
 *
 * ## Written about this service specifically
 *
 * Generic boilerplate would have been faster and would have described a
 * different product. These terms name the things that actually exist here -
 * uploads and transcoding, live rooms and their queues, earned boosts, the
 * explicit flag, the copyright process - because a term that does not match the
 * software is unenforceable in the only way that matters: nobody can follow it.
 *
 * The operating entity, governing law and contact addresses come from
 * `legal.ts` rather than being written into the prose, so changing who is
 * behind the service is one file.
 */
export function TermsPage() {
  useDocumentTitle('Terms');

  return (
    <LegalPage
      title="Terms of Service"
      summary={`Upload music you have the right to upload, treat people decently, and do not
        try to game the charts or the queues. Your music stays yours - you give us only the
        permission needed to store it, convert it and play it to the people you choose. If
        somebody claims your work infringes theirs, you get told and you get to answer. If you
        break these terms we may remove content or close your account, and we will tell you why.`}
    >
      <Clause id="agreement" title="1. The agreement">
        <p>
          These terms are between you and {legal.operator} ("we", "us"), the operator of{' '}
          {brand.name} at {brand.domain} (the "service"). By creating an account or using the
          service you accept them. If you do not accept them, do not use the service.
        </p>
        <p>
          We may change these terms. If a change materially affects your rights we will tell you
          in the product before it takes effect, and continuing to use the service after that date
          means you accept the revised terms. The date at the top of this page is always the
          version in force.
        </p>
      </Clause>

      <Clause id="account" title="2. Your account">
        <Points>
          <li>
            You must be at least 13 years old, or 16 where local law requires a higher age for
            consent to processing your data.
          </li>
          <li>
            One person, one account. You are responsible for what happens under yours, so keep
            your password to yourself and tell us promptly if you think somebody else has it.
          </li>
          <li>
            Your handle is yours while you use it. We may reclaim handles that impersonate
            somebody, that were registered to sell, or that belong to accounts abandoned for a
            long period.
          </li>
          <li>
            You may close your account at any time. We may close it if you break these terms, if
            we are required to, or if operating it becomes unlawful.
          </li>
        </Points>
      </Clause>

      <Clause id="your-content" title="3. Your music stays yours">
        <p>
          You keep every right you had in anything you upload. We claim no ownership of your
          recordings, artwork, writing or anything else you put here.
        </p>
        <p>
          To run the service we need your permission to do specific technical things with it. You
          grant us a non-exclusive, worldwide, royalty-free license to store your content, make
          copies needed to serve it, convert it into other formats and bitrates for streaming,
          generate artwork sizes and waveform data from it, and transmit it to listeners you have
          made it available to. That license exists only to operate the service and lasts only as
          long as you keep the content here.
        </p>
        <p>
          You also allow other people to use the ordinary features of the service on your public
          content - to play it, save it, repost it, comment on it, and submit it to a host's live
          room. Setting a track to unlisted or private limits who can do that.
        </p>
        <p>
          When you delete something, we stop serving it and remove it from our systems, including
          backups, within 30 days. Copies other people made outside the service are outside our
          control, and posts referring to deleted content may keep a reference to something that
          no longer loads.
        </p>
      </Clause>

      <Clause id="rights" title="4. What you must have the right to upload">
        <p>
          You may only upload material you own or are licensed to distribute. That includes every
          layer of it: the recording, the composition, any sample, any interpolation, any vocal,
          any artwork.
        </p>
        <p>
          Uploading a sample you did not clear, a bootleg, a remix without permission, or somebody
          else's record under your own name is a breach of these terms whether or not anybody
          complains, and whether or not you profit from it.
        </p>
        <p>
          If you mark a track as containing explicit content, you are telling us so and we will
          label it. Failing to mark something explicit is not fraud, but repeatedly mislabelling
          material to get past hosts who have opted out of it is.
        </p>
      </Clause>

      <Clause id="conduct" title="5. What you must not do">
        <Points>
          <li>
            Upload material that is unlawful, that sexualises minors, that incites violence, or
            that exists to harass a person or group.
          </li>
          <li>
            Impersonate somebody, or present yourself as connected to an artist, label or brand
            you have nothing to do with.
          </li>
          <li>
            Inflate plays, saves, reposts, followers, discovery credit or queue position by
            automated means, coordinated groups, or any other arrangement whose purpose is to make
            numbers say something untrue. This includes creating accounts to boost your own work.
          </li>
          <li>
            Scrape the service, circumvent the media token system, re-host our audio elsewhere, or
            interfere with anybody else's use of it.
          </li>
          <li>
            Use another person's material to file a copyright claim, or file one you know to be
            unfounded.
          </li>
        </Points>
      </Clause>

      <Clause id="rooms" title="6. Live rooms">
        <p>
          Hosting is granted by application and can be withdrawn. A host chooses what plays in
          their room, who may submit to it, and whether it accepts tracks marked explicit. They
          are responsible for what they broadcast and for moderating the chat in their room.
        </p>
        <p>
          Submitting a track to a room is a request, not a booking. A host may decline it, skip it
          or remove it for any reason. Playing a track in a room does not transfer any rights in
          it, and a host must have the right to broadcast anything they add themselves.
        </p>
        <p>
          Anything said in room chat is public to everybody in that room. Hosts and our moderators
          may remove messages; removed messages leave a visible marker rather than disappearing,
          so a moderated conversation still reads honestly.
        </p>
      </Clause>

      <Clause id="boosts" title="7. Boosts and discovery credit">
        <p>
          Discovery credit is earned by putting other artists' work in front of hosts who play it
          through. It converts into boosts that raise a submission's position in a queue.
        </p>
        <p>
          Credit and boosts have no monetary value, cannot be bought, sold, transferred or
          exchanged for anything, and are not your property. We may adjust balances to correct
          errors or to reverse credit obtained by breaking clause 5, and we may change how they
          are earned or what they do. If we discontinue them entirely, unspent boosts simply cease
          to exist and nothing is owed for them.
        </p>
      </Clause>

      <Clause id="copyright" title="8. Copyright claims and repeat infringement">
        <p>
          If you believe something here infringes your copyright, tell us using the{' '}
          <Link to="/dmca" className="text-accent hover:underline">
            copyright notice form
          </Link>
          . A person reviews every claim. Nothing is removed automatically, because a form anybody
          can submit must not be able to silence somebody's work on its own.
        </p>
        <p>
          If we act on a claim, the recording is taken down and the uploader is told why. They may
          file a counter-notice, which we forward to the claimant; if the claimant does not pursue
          the matter, the recording goes back up.
        </p>
        <p>
          A claim we uphold results in a strike against the account, which counts for{' '}
          {legal.strikeMonths} months. A strike is removed if a counter-notice reverses the
          decision. Accounts that accumulate strikes repeatedly will be closed under our
          repeat-infringer policy.
        </p>
        <p>Copyright correspondence: {legal.contact.copyright}</p>
      </Clause>

      <Clause id="our-content" title="9. The service itself">
        <p>
          The software, design, name and logo of {brand.name} are ours. These terms give you no
          rights in them beyond using the service as intended. Do not copy our interface, present
          another service as ours, or use our name in a way that suggests we endorse you.
        </p>
      </Clause>

      <Clause id="moderation" title="10. Removal and suspension">
        <p>
          We may remove content or restrict an account that breaches these terms, that we are
          legally required to act on, or that presents a serious risk to other people. Where we
          can, we will tell you what was removed and why.
        </p>
        <p>
          Serious cases - material involving minors, credible threats, or coordinated attacks on
          the service - may be actioned immediately and without notice.
        </p>
        <p>
          If you think we got it wrong, reply to the message we sent you. A different person will
          look at it.
        </p>
      </Clause>

      <Clause id="availability" title="11. Availability and liability">
        <p>
          The service is provided as it is. We work to keep it running and your uploads intact,
          but we do not promise it will be uninterrupted, that transcoding will always succeed, or
          that nothing will ever be lost. Keep your own copies of your masters. We are not your
          archive.
        </p>
        <p>
          To the extent the law allows, we are not liable for indirect or consequential loss,
          including lost income, lost opportunities or lost data. Nothing in these terms limits
          liability for death or personal injury caused by negligence, for fraud, or for anything
          else that cannot lawfully be limited.
        </p>
        <p>
          Where you have statutory consumer rights, these terms do not reduce them.
        </p>
      </Clause>

      <Clause id="law" title="12. Governing law">
        <p>
          These terms are governed by the laws of {legal.jurisdiction}, and disputes will be heard
          there - unless the consumer law where you live gives you the right to bring a claim
          locally, in which case that right stands.
        </p>
        <p>
          If any clause turns out to be unenforceable, the rest continues to apply.
        </p>
      </Clause>

      <Clause id="contact" title="13. Reaching us">
        <p>
          General questions: {legal.contact.support}
          <br />
          Privacy and data requests: {legal.contact.privacy}
          <br />
          Copyright: {legal.contact.copyright}
        </p>
        <p>
          See also our{' '}
          <Link to="/privacy" className="text-accent hover:underline">
            Privacy Policy
          </Link>
          , which explains what we collect and why.
        </p>
      </Clause>
    </LegalPage>
  );
}
